import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { initShareReceiver } from "@/lib/shareReceiver";
import { setPushNotificationNavigate } from "@/lib/push";

import LoginPage from "@/pages/client/Login";
import RegisterPage from "@/pages/client/Register";
import AccountPage from "@/pages/client/Account";
import ForgotPasswordPage from "@/pages/client/ForgotPassword";
import ResetPasswordPage from "@/pages/client/ResetPassword";
import ClientHome from "@/pages/client/Home";
import SendPage from "@/pages/client/Send";
import MyCodes from "@/pages/client/MyCodes";
import OrderDetail from "@/pages/client/OrderDetail";
import { QuotePage, ContactPage, HelpPage } from "@/pages/client/Misc";
import ClubPage from "@/pages/client/Club";
import AssistantPage from "@/pages/client/Assistant";
import PublicPaymentPage from "@/pages/client/PublicPayment";

import StaffLogin from "@/pages/staff/StaffLogin";
import StaffLayout from "@/pages/staff/StaffLayout";
import AdminOrders from "@/pages/staff/AdminOrders";
import AdminClients from "@/pages/staff/AdminClients";
import AdminSettings from "@/pages/staff/AdminSettings";
import AdminMessages from "@/pages/staff/AdminMessages";
import AdminStats from "@/pages/staff/AdminStats";
import AdminPromotions from "@/pages/staff/AdminPromotions";
import AdminAIInbox from "@/pages/staff/AdminAIInbox";
import AdminServiceCatalog from "@/pages/staff/AdminServiceCatalog";
import AdminRaffles from "@/pages/staff/AdminRaffles";
import AdminPayments from "@/pages/staff/AdminPayments";
import AdminBudgets from "@/pages/staff/AdminBudgets";

function RequireAuth({ children, role }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="flex h-screen items-center justify-center text-lg text-slate-600">
        Cargando…
      </div>
    );
  }
  if (!user) {
    const to = role === "staff" ? "/staff/login" : "/login";
    return <Navigate to={to} state={{ from: location }} replace />;
  }
  if (role === "staff" && user.role !== "admin" && user.role !== "staff") {
    return <Navigate to="/staff/login" replace />;
  }
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <div className="p-8 text-slate-600">Cargando…</div>;
  if (user && (user.role === "admin" || user.role === "staff")) {
    return <Navigate to="/staff" replace />;
  }
  return <ClientHome />;
}

// Bootstraps the native share-intent bridge. On the PWA it's a noop because
// shareReceiver early-returns when !Capacitor.isNativePlatform().
function ShareBootstrapper() {
  const navigate = useNavigate();
  useEffect(() => {
    initShareReceiver(navigate);
    setPushNotificationNavigate(navigate);
  }, [navigate]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-center" richColors closeButton />
          <ShareBootstrapper />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/send" element={<SendPage />} />
            <Route path="/share" element={<SendPage />} />
            <Route path="/codes" element={<MyCodes />} />
            <Route path="/codes/:code" element={<OrderDetail />} />
            <Route
              path="/quote"
              element={<RequireAuth><QuotePage /></RequireAuth>}
            />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/club" element={<RequireAuth><ClubPage /></RequireAuth>} />
            <Route path="/assistant" element={<AssistantPage />} />
            <Route path="/pagos/:code" element={<PublicPaymentPage />} />

            {/* Staff */}
            <Route path="/staff/login" element={<StaffLogin />} />
            <Route
              path="/staff"
              element={<RequireAuth role="staff"><StaffLayout /></RequireAuth>}
            >
              <Route index element={<AdminOrders />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="stats" element={<AdminStats />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="promotions" element={<AdminPromotions />} />
              <Route path="raffles" element={<AdminRaffles />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="budgets" element={<AdminBudgets />} />
              <Route path="service-catalog" element={<AdminServiceCatalog />} />
              <Route path="ai-inbox" element={<AdminAIInbox />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
